import React from "react";

import _ from "lodash";

import useForm from "commons/hooks/useForm";

import { FormHookType } from "commons/types/form";
import { ObjectPropertyType } from "commons/types/validation";

import { notEmptyValidator } from "commons/utils/validate";

import { AttachmentType } from "components/ui/AttachmentPicker";

import useCatalogApi from "hooks/api/private/useCatalogApi";
import { useCatalogId } from "hooks/useId";
import useMobileOrDesktopFile from "hooks/useMobileOrDesktopFile";
import useSingleFile from "hooks/useSingleFile";
import useSnackbar from "hooks/useSnackbar";

import { CatalogBody, CatalogDetails } from "types/api/catalog";
import { DesktopMobileForm, LanguageEnum } from "types/api/commons";
import { DesktopOrMobile } from "types/enums";

import { converterAttachmentToFileWithPayload, createAttachmentType } from "utils/utils";
import {
    fileNotEmptyValidator,
    fileProperties,
    fileSizeValidator,
    getExtraErrorsDescription,
    notEmptyMedia,
} from "utils/validators";

export type FormCatalogHook = {
    setDesktopMobileImage: (fileToSet: AttachmentType, target: DesktopOrMobile) => void;
    setSingleFile: (fileToSet: AttachmentType, path: string) => void;
    buttonSave: () => void;
    buttonUpdate: () => void;
    formHook: FormHookType<CatalogBodyForm>;
};

type CatalogBodyForm = {
    front_visible: boolean;
    reserved_area_visible: boolean;
    graphical_order: number | null;
    file: AttachmentType;
    cover_img: DesktopMobileForm;
    text: {
        [x in LanguageEnum]: {
            title: string;
            title_plain_text: string;
        };
    };
};

function useCatalogForm(): FormCatalogHook {
    const catalogId = useCatalogId();

    const {
        enqueueSuccessSnackbarThenNavigate,
        enqueueSuccessSnackbar,
        enqueueErrorSnackbar,
        enqueueFormErrorSnackbar,
    } = useSnackbar();

    const { createCatalog, updateCatalog, getOneCatalog } = useCatalogApi();

    const schema: ObjectPropertyType = React.useMemo(() => {
        return {
            type: "object",
            properties: {
                graphical_order: {
                    type: "simple",
                    validators: [notEmptyValidator],
                },
                file: {
                    type: "object",
                    validators: [fileNotEmptyValidator, fileSizeValidator],
                    properties: fileProperties,
                },
                cover_img: notEmptyMedia,
                text: {
                    type: "collection",
                    of: {
                        type: "object",
                        properties: {
                            title: {
                                type: "simple",
                                validators: [notEmptyValidator],
                            },
                        },
                    },
                },
            },
        };
    }, []);

    const initialValues: CatalogBodyForm = React.useMemo(() => {
        return {
            front_visible: false,
            reserved_area_visible: false,
            graphical_order: null,
            file: createAttachmentType({ label: "File PDF" }),
            cover_img: {
                id: undefined,
                desktop: createAttachmentType({ label: "Desktop" }),
                mobile: createAttachmentType({ label: "Mobile" }),
            },
            text: {
                it: {
                    title: "",
                    title_plain_text: "",
                },
                en: {
                    title: "",
                    title_plain_text: "",
                },
            },
        };
    }, []);

    const formHook = useForm<CatalogBodyForm>(schema, initialValues, getExtraErrorsDescription);

    const { showAllError, setForm, isValid, form } = formHook;

    const catalogBody: CatalogBody = React.useMemo(() => {
        const body: CatalogBody = {
            front_visible: form.front_visible,
            reserved_area_visible: form.reserved_area_visible,
            graphical_order: form.graphical_order ?? 0,
            cover_img: {
                id: form.cover_img.id,
                desktop: converterAttachmentToFileWithPayload(form.cover_img.desktop),
                mobile: converterAttachmentToFileWithPayload(form.cover_img.mobile),
            },
            file: converterAttachmentToFileWithPayload(form.file),
            text: {
                it: {
                    title: form.text.it.title,
                },
                en: {
                    title: form.text.en.title,
                },
            },
        };

        return body;
    }, [form]);

    const [catalogDetails, setCatalogDetails] = React.useState<CatalogDetails | undefined>(
        undefined
    );

    React.useEffect(() => {
        if (catalogId) {
            const [apiCall, abortController] = getOneCatalog({ id: catalogId });

            apiCall.then(setCatalogDetails);

            return () => abortController.abort();
        }
    }, [getOneCatalog, setForm, catalogId]);

    const buttonSave = React.useCallback(() => {
        if (isValid) {
            const [apiCall] = createCatalog(catalogBody);

            apiCall
                .then(() => {
                    enqueueSuccessSnackbarThenNavigate(
                        "Catalogo creato con successo",
                        "/cataloghi"
                    );
                })
                .catch(() => enqueueErrorSnackbar("Errore nella creazione del catalogo"));
        } else {
            showAllError();

            enqueueFormErrorSnackbar();
        }
    }, [
        isValid,
        createCatalog,
        catalogBody,
        enqueueSuccessSnackbarThenNavigate,
        enqueueErrorSnackbar,
        showAllError,
        enqueueFormErrorSnackbar,
    ]);

    const buttonUpdate = React.useCallback(() => {
        if (isValid) {
            const [apiCall] = updateCatalog({ id: catalogId }, catalogBody);

            apiCall
                .then(() => {
                    enqueueSuccessSnackbar("Catalogo modificato con successo");
                })
                .catch(() => enqueueErrorSnackbar("Errore nella modifica del catalogo"));
        } else {
            showAllError();

            enqueueFormErrorSnackbar();
        }
    }, [
        isValid,
        updateCatalog,
        catalogId,
        catalogBody,
        enqueueSuccessSnackbar,
        enqueueErrorSnackbar,
        showAllError,
        enqueueFormErrorSnackbar,
    ]);

    const { setDesktopMobileImage } = useMobileOrDesktopFile({
        setForm: setForm,
        path: "cover_img",
    });

    const { setSingleFile } = useSingleFile({ setForm: setForm });

    React.useEffect(() => {
        if (catalogDetails) {
            setForm((prevForm) => {
                const newForm = _.cloneDeep(prevForm);

                newForm.front_visible = catalogDetails.front_visible;
                newForm.reserved_area_visible = catalogDetails.reserved_area_visible;
                newForm.graphical_order = catalogDetails.graphical_order;
                newForm.text.en.title = catalogDetails.text.en.title;
                newForm.text.it.title = catalogDetails.text.it.title;
                newForm.text.it.title = catalogDetails.text.it.title;
                newForm.text.en.title = catalogDetails.text.en.title;

                newForm.file = createAttachmentType({
                    id: catalogDetails.file.id,
                    label: "File PDF",
                    filename: catalogDetails.file.filename,
                    status: 2,
                });

                newForm.cover_img = {
                    id: catalogDetails.cover_img.id,
                    desktop: createAttachmentType({
                        id: catalogDetails.cover_img.desktop.id,
                        label: "Desktop",
                        filename: catalogDetails.cover_img.desktop.filename,
                        status: 2,
                    }),
                    mobile: createAttachmentType({
                        id: catalogDetails.cover_img.mobile.id,
                        label: "Mobile",
                        filename: catalogDetails.cover_img.mobile.filename,
                        status: 2,
                    }),
                };

                return newForm;
            });
        }
    }, [setForm, catalogDetails, catalogId]);

    return React.useMemo(
        (): FormCatalogHook => ({
            buttonUpdate,
            buttonSave,
            setDesktopMobileImage,
            setSingleFile,
            formHook,
        }),
        [buttonUpdate, buttonSave, setDesktopMobileImage, setSingleFile, formHook]
    );
}

export default useCatalogForm;
